/* eslint-env browser */
/* eslint-disable no-console -- SDK example / debugging */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  IconButton,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { JustCallDialer } from '@justcall/justcall-dialer-sdk';
import { UserStatus } from '@marageti/z4-sdk/lib/auth';
import { Traveler } from '@marageti/z4-sdk/lib/people';
import Draggable, { DraggableProps } from 'react-draggable';
import { appNavigate } from '../routes/app-navigate';
import justcallFaviconUrl from '../assets/png/justcall-favicon.png';
import justcallNotificationFaviconUrl from '../assets/png/justcall-notification-favicon.png';
import useTravelersSearch from './use-travelers-search';
import { useExclusiveWebLock } from './use-exclusive-web-lock';

const DraggablePanel = Draggable as React.ComponentType<Partial<DraggableProps>>;

const dialerContainerId = 'justcall-dialer-container';

/** Exclusive across all tabs/windows on this origin so only one JustCall SDK instance exists. */
const JUSTCALL_DIALER_LOCK_NAME = 'oryx-justcall-dialer';

/** Default `document.title` (matches `public/index.html`). Reset when the dialer SDK is not mounted in this tab. */
const APP_DOCUMENT_TITLE = 'Oryx';

/** Default favicon (matches `public/index.html` `shortcut icon`). */
const APP_FAVICON_HREF = `${process.env.PUBLIC_URL ?? ''}/oryx-favicon.png`;

function applyDocumentFavicon(href: string) {
  const links = document.querySelectorAll<HTMLLinkElement>(
    'link[rel="icon"], link[rel="shortcut icon"]',
  );
  if (links.length > 0) {
    for (let i = 0; i < links.length; i += 1) {
      links[i].href = href;
    }
    return;
  }
  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = 'image/png';
  link.href = href;
  document.head.appendChild(link);
}

/** Fixed dialer chrome size (header + SDK mount share width). */
const DIALER_PANEL_WIDTH = 365;
const DIALER_PANEL_MAX_HEIGHT = 615;

/** Outer node for `react-draggable` (fixed origin + translate). */
const dialerDragRootSx = {
  position: 'fixed' as const,
  top: 16,
  right: 16,
  zIndex: (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 1,
  width: DIALER_PANEL_WIDTH,
  maxWidth: 'calc(100vw - 32px)',
  boxSizing: 'border-box' as const,
  display: 'flex',
  flexDirection: 'column' as const,
  boxShadow: 2,
};

/** JustCall SDK mount below header (same column as {@link dialerDragRootSx}). */
const dialerSdkMountVisibleSx = {
  width: '100%',
  height: `min(${DIALER_PANEL_MAX_HEIGHT}px, calc(100vh - 72px))`,
  boxSizing: 'border-box' as const,
  bgcolor: 'background.paper',
  borderRadius: (theme: { shape: { borderRadius: number } }) => `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
  boxShadow: 8,
  overflow: 'hidden' as const,
};

/** Fills hidden wrapper so the SDK keeps a stable `#dialerContainerId` node. */
const dialerSdkMountHiddenSx = {
  width: '100%',
  height: '100%',
  boxSizing: 'border-box' as const,
  overflow: 'hidden' as const,
};

/** Off-screen mount while signed in but the panel is closed. */
const dialerMountHiddenSx = {
  position: 'fixed' as const,
  top: 0,
  left: 0,
  width: DIALER_PANEL_WIDTH,
  height: DIALER_PANEL_MAX_HEIGHT,
  opacity: 0,
  visibility: 'hidden' as const,
  pointerEvents: 'none' as const,
  zIndex: -1,
  overflow: 'hidden' as const,
  boxSizing: 'border-box' as const,
};

type InboundCallerLookupState = {
  searchPhone: string;
  travelers: Traveler[] | undefined;
  isLoading: boolean;
  error: unknown;
  totalHits: number;
};

const defaultInboundCallerLookup: InboundCallerLookupState = {
  searchPhone: '',
  travelers: undefined,
  isLoading: false,
  error: undefined,
  totalHits: 0,
};

type JustCallDialerContextValue = {
  /** JustCall SDK is mounted (user signed in from the phone menu). */
  isDialerMounted: boolean;
  /** True while the SDK reports an incoming or outgoing ring (until answered or the attempt ends). */
  isRinging: boolean;
  /** True from {@link JustCallDialer} `call-answered` until `call-ended`. */
  callActive: boolean;
  /**
   * True when this tab is trying to use the dialer but another tab/window holds the lock.
   * Use {@link retryJustCallDialerLock} after closing the other tab or signing out there.
   */
  isDialerLockBlocked: boolean;
  /**
   * True when the exclusive dialer lock is held in another tab/window (contention or idle probe).
   * Use for status UI; {@link isDialerLockBlocked} is for the blocked-session menu (Try again / Cancel).
   */
  isDialerLockHeldRemotely: boolean;
  retryJustCallDialerLock: () => void;
  signInToDialer: () => void;
  openDialer: () => void;
  signOutFromDialer: () => void;
  launchDialerWithNumber: (number: string) => void;
  closeDialer: () => void;
  inboundCallerLookup: InboundCallerLookupState;
};

const JustCallDialerContext = createContext<JustCallDialerContextValue | null>(null);

function travelersFullTextSearchPath(phone: string): string {
  const q = new URLSearchParams({
    fullText: phone,
    status: UserStatus.ENABLED,
  });
  return `/travelers?${q.toString()}`;
}

function inboundMatchTravelerLabel(traveler: Traveler): string {
  const name = traveler.fullName?.trim();
  return name || 'View traveler';
}

async function runDialNumber(
  dialer: JustCallDialer,
  number: string,
  onTravelerPhoneForLookup?: (phone: string | null) => void,
) {
  const trimmed = number.trim();
  if (!trimmed) return;
  onTravelerPhoneForLookup?.(trimmed);
  try {
    await dialer.ready();
    dialer.dialNumber(trimmed);
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    console.error('JustCall dialNumber failed:', message);
  }
}

/** Traveler phone from JustCall `call-ringing` (`from` / `to` are typically E.164, e.g. `+16157192157`). */
function getTravelerNumberFromCall(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const { direction, from, to } = data as {
    direction?: unknown;
    from?: unknown;
    to?: unknown;
  };
  if (typeof direction !== 'string') return null;
  const d = direction.trim().toLowerCase();
  let raw: unknown;
  if (d === 'inbound') {
    raw = from;
  } else if (d === 'outbound') {
    raw = to;
  } else {
    return null;
  }
  if (typeof raw !== 'string') return null;
  const t = raw.trim();
  return t || null;
}

function InboundCallerTravelerSearchBridge({
  searchPhone,
  onLookupUpdate,
}: {
  searchPhone: string;
  onLookupUpdate: (next: InboundCallerLookupState) => void;
}) {
  const queryValues = useMemo(
    () => ({
      countryOfResidence: [] as string[],
      fullText: searchPhone,
      order: '',
      sort: '',
      status: [UserStatus.ENABLED] as string[],
      tags: [] as string[],
    }),
    [searchPhone],
  );

  const {
    travelers,
    error,
    isLoading,
    totalHits,
  } = useTravelersSearch(queryValues);

  useEffect(() => {
    onLookupUpdate({
      searchPhone,
      travelers,
      isLoading,
      error,
      totalHits,
    });
  }, [travelers, isLoading, error, totalHits, searchPhone, onLookupUpdate]);

  return null;
}

function JustCallDialerHost({
  visible,
  onRequestClose,
  claimPendingDialNumber,
  dialRequestSeq,
  openDialerIfHidden,
  onInboundCallerPhone,
  inboundCallerLookup,
  onSdkCallRinging,
  onSdkCallAnswered,
  onSdkCallEnded,
}: {
  visible: boolean;
  onRequestClose: () => void;
  claimPendingDialNumber: () => string | null;
  dialRequestSeq: number;
  openDialerIfHidden: () => void;
  /** Pass `null` to clear lookup (unknown caller or call ended). */
  onInboundCallerPhone: (phone: string | null) => void;
  inboundCallerLookup: InboundCallerLookupState;
  onSdkCallRinging: () => void;
  onSdkCallAnswered: () => void;
  onSdkCallEnded: () => void;
}) {
  const [initError, setInitError] = useState<string | null>(null);
  const dialerInstance = useRef<JustCallDialer | undefined>();
  const dialerDragNodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (dialerInstance.current) {
      return () => undefined;
    }

    setInitError(null);

    try {
      const dialer = new JustCallDialer({
        dialerId: dialerContainerId,
        onReady: () => {
          const num = claimPendingDialNumber();
          if (num) {
            runDialNumber(dialer, num, onInboundCallerPhone).catch(() => undefined);
          }
        },
      });
      /**
       * `call-ringing` payload (example):
       * - call_sid: "CA8f29a5c100d8619bb2f27d56999cc9d5"
       * - direction: "inbound" | "outbound"
       * - from: "+16157192157"
       * - to: "+12722144510"
       */
      dialer.on('call-ringing', (data: unknown) => {
        onSdkCallRinging();
        openDialerIfHidden();
        const travelerPhone = getTravelerNumberFromCall(data);
        if (travelerPhone) {
          onInboundCallerPhone(travelerPhone);
        }
      });
      /**
       * `call-answered` payload (example):
       * - call_sid: "CAb2bd270ca8399d56caad3ab419cecd92"
       * - direction: "inbound" | "outbound"
       * - from: "+16157192157"
       * - to: "+12722144510"
       */
      dialer.on('call-answered', () => {
        onSdkCallAnswered();
      });

      /**
       * call-ended payload (example):
       * - call_sid: "CAb2bd270ca8399d56caad3ab419cecd92"
       * - direction: "inbound"
       * - duration: 6
       */
      dialer.on('call-ended', () => {
        onSdkCallEnded();
        onInboundCallerPhone(null);
      });
      /**
       * `sms-received` payload (example):
       * - body: "Test again"
       * - from: "12722144510"
       * - message_sid: 534327806
       * - received_at: "2026-04-02T17:52:29.000Z"
       * - to: "16157192157"
       */
      dialer.on('sms-received', () => undefined);

      dialerInstance.current = dialer;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      setInitError(`Setup Error: ${message}. Please contact support.`);
    }

    return () => {
      if (dialerInstance.current) {
        console.log('Destroying dialer instance.');
        dialerInstance.current.destroy();
        dialerInstance.current = undefined;
      }
    };
  }, [
    claimPendingDialNumber,
    openDialerIfHidden,
    onInboundCallerPhone,
    onSdkCallRinging,
    onSdkCallAnswered,
    onSdkCallEnded,
  ]);

  useEffect(() => {
    if (!visible) return;
    const dialer = dialerInstance.current;
    if (!dialer) return;
    const num = claimPendingDialNumber();
    if (!num) return;
    runDialNumber(dialer, num, onInboundCallerPhone).catch(() => undefined);
  }, [visible, dialRequestSeq, claimPendingDialNumber, onInboundCallerPhone]);

  const handleCloseClick = () => {
    onRequestClose();
  };

  const centerStatus = useMemo(() => {
    if (initError) return initError;
    const {
      searchPhone, travelers, isLoading, totalHits, error,
    } = inboundCallerLookup;
    if (!searchPhone) return '';
    if (isLoading) return 'Looking up traveler…';
    if (error) return 'Traveler lookup failed';
    if (totalHits === 1 && travelers?.[0]) {
      const match = travelers[0];
      const { id } = match;
      return (
        <MuiLink
          component="button"
          type="button"
          variant="caption"
          color="inherit"
          underline="hover"
          onClick={() => appNavigate(`/travelers/${id}`)}
          sx={{
            verticalAlign: 'baseline',
            textAlign: 'inherit',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {inboundMatchTravelerLabel(match)}
        </MuiLink>
      );
    }
    if (totalHits > 1) {
      const to = travelersFullTextSearchPath(searchPhone);
      return (
        <MuiLink
          component="button"
          type="button"
          variant="caption"
          color="inherit"
          underline="hover"
          onClick={() => appNavigate(to)}
          sx={{
            verticalAlign: 'baseline',
            textAlign: 'inherit',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          Multiple travelers — open search
        </MuiLink>
      );
    }
    return 'Unknown traveler';
  }, [initError, inboundCallerLookup]);

  return (
    <DraggablePanel
      disabled={!visible}
      nodeRef={dialerDragNodeRef}
      handle=".justcall-dialer-drag-handle"
      cancel="button, a"
      bounds="body"
      defaultPosition={{ x: 0, y: 0 }}
    >
      <Box
        ref={dialerDragNodeRef}
        sx={visible ? dialerDragRootSx : dialerMountHiddenSx}
      >
        {visible ? (
          <Paper
            elevation={8}
            sx={{
              width: '100%',
              boxSizing: 'border-box',
              borderRadius: (theme) => `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
              overflow: 'hidden',
            }}
          >
            <Stack
              className="justcall-dialer-drag-handle"
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{
                width: '100%',
                boxSizing: 'border-box',
                px: 1.5,
                py: 1,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'grey.100',
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
              }}
            >
              <Typography variant="subtitle2" component="span" noWrap>
                JustCall
              </Typography>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                component="div"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  textAlign: 'center',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: inboundCallerLookup.searchPhone && !initError ? 'normal' : 'nowrap',
                }}
              >
                {centerStatus}
              </Typography>
              <IconButton
                type="button"
                size="small"
                aria-label="Close dialer"
                onClick={handleCloseClick}
                edge="end"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Paper>
        ) : null}
        <Box
          id={dialerContainerId}
          component="div"
          sx={visible ? dialerSdkMountVisibleSx : dialerSdkMountHiddenSx}
          aria-hidden={!visible}
        />
      </Box>
    </DraggablePanel>
  );
}

export function JustCallDialerProvider({ children }: { children: React.ReactNode }) {
  /** User wants a dialer session (sign-in or launch-with-number); SDK mounts only when the tab holds the Web Lock. */
  const [sessionRequested, setSessionRequested] = useState(false);
  const [visible, setVisible] = useState(false);
  const [dialRequestSeq, setDialRequestSeq] = useState(0);
  const [inboundSearchPhone, setInboundSearchPhone] = useState('');
  const [inboundCallerLookup, setInboundCallerLookup] = useState<InboundCallerLookupState>(
    () => ({ ...defaultInboundCallerLookup }),
  );
  const [isRinging, setIsRinging] = useState(false);
  const [callActive, setCallActive] = useState(false);
  const pendingDialRef = useRef<string | null>(null);

  const {
    lockHeld,
    isContentionBlocked,
    isLockHeldElsewhereWhileIdle,
    retryAcquire,
    requestAcquireBump,
  } = useExclusiveWebLock(JUSTCALL_DIALER_LOCK_NAME, sessionRequested, {
    onMissingApi: () => {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Web Locks API unavailable; JustCall dialer may run in multiple tabs.');
      }
    },
  });

  const dialerMounted = lockHeld;
  const isDialerLockHeldRemotely = isContentionBlocked || isLockHeldElsewhereWhileIdle;

  const onSdkCallRinging = useCallback(() => {
    setIsRinging(true);
  }, []);

  const onSdkCallAnswered = useCallback(() => {
    setIsRinging(false);
    setCallActive(true);
  }, []);

  const onSdkCallEnded = useCallback(() => {
    setIsRinging(false);
    setCallActive(false);
  }, []);

  useEffect(() => {
    if (!lockHeld) {
      setIsRinging(false);
      setCallActive(false);
    }
  }, [lockHeld]);

  useEffect(() => {
    if (!dialerMounted) {
      document.title = APP_DOCUMENT_TITLE;
      applyDocumentFavicon(APP_FAVICON_HREF);
      return;
    }
    if (isRinging) {
      document.title = '(Call Ringing...) Oryx';
      applyDocumentFavicon(justcallNotificationFaviconUrl);
      return;
    }
    if (callActive) {
      document.title = '(Call Active) Oryx';
      applyDocumentFavicon(justcallFaviconUrl);
      return;
    }
    document.title = '(Phone Ready) Oryx';
    applyDocumentFavicon(justcallFaviconUrl);
  }, [dialerMounted, isRinging, callActive]);

  useEffect(() => {
    if (!lockHeld) {
      return undefined;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Browsers ignore custom strings and show a generic leave-site confirmation.
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [lockHeld]);

  const claimPendingDialNumber = useCallback((): string | null => {
    const n = pendingDialRef.current;
    pendingDialRef.current = null;
    return n;
  }, []);

  const signInToDialer = useCallback(() => {
    pendingDialRef.current = null;
    setSessionRequested(true);
    setVisible(true);
    requestAcquireBump();
  }, [requestAcquireBump]);

  const openDialer = useCallback(() => {
    if (!dialerMounted) return;
    pendingDialRef.current = null;
    setVisible(true);
  }, [dialerMounted]);

  const signOutFromDialer = useCallback(() => {
    pendingDialRef.current = null;
    setVisible(false);
    setSessionRequested(false);
    setInboundSearchPhone('');
    setInboundCallerLookup({ ...defaultInboundCallerLookup });
    setIsRinging(false);
    setCallActive(false);
  }, []);

  const launchDialerWithNumber = useCallback((number: string) => {
    const n = number.trim();
    if (!n) return;
    pendingDialRef.current = n;
    setSessionRequested(true);
    setDialRequestSeq((s) => s + 1);
    setVisible(true);
    requestAcquireBump();
  }, [requestAcquireBump]);

  const closeDialer = useCallback(() => {
    setVisible(false);
    setInboundSearchPhone('');
    setInboundCallerLookup({ ...defaultInboundCallerLookup });
  }, []);

  const openDialerIfHidden = useCallback(() => {
    setVisible(true);
  }, []);

  const onInboundCallerPhone = useCallback((phone: string | null) => {
    setInboundSearchPhone(phone ?? '');
    if (!phone) {
      setInboundCallerLookup({ ...defaultInboundCallerLookup });
    }
  }, []);

  const onInboundLookupUpdate = useCallback((next: InboundCallerLookupState) => {
    setInboundCallerLookup(next);
  }, []);

  const value = useMemo(
    () => ({
      isDialerMounted: dialerMounted,
      isRinging,
      callActive,
      isDialerLockBlocked: isContentionBlocked,
      isDialerLockHeldRemotely,
      retryJustCallDialerLock: retryAcquire,
      signInToDialer,
      openDialer,
      signOutFromDialer,
      launchDialerWithNumber,
      closeDialer,
      inboundCallerLookup,
    }),
    [
      dialerMounted,
      isRinging,
      callActive,
      isContentionBlocked,
      isDialerLockHeldRemotely,
      retryAcquire,
      signInToDialer,
      openDialer,
      signOutFromDialer,
      launchDialerWithNumber,
      closeDialer,
      inboundCallerLookup,
    ],
  );

  return (
    <JustCallDialerContext.Provider value={value}>
      <InboundCallerTravelerSearchBridge
        searchPhone={inboundSearchPhone}
        onLookupUpdate={onInboundLookupUpdate}
      />
      {children}
      {lockHeld ? (
        <JustCallDialerHost
          visible={visible}
          onRequestClose={closeDialer}
          claimPendingDialNumber={claimPendingDialNumber}
          dialRequestSeq={dialRequestSeq}
          openDialerIfHidden={openDialerIfHidden}
          onInboundCallerPhone={onInboundCallerPhone}
          inboundCallerLookup={inboundCallerLookup}
          onSdkCallRinging={onSdkCallRinging}
          onSdkCallAnswered={onSdkCallAnswered}
          onSdkCallEnded={onSdkCallEnded}
        />
      ) : null}
    </JustCallDialerContext.Provider>
  );
}

export function useJustCallDialer(): JustCallDialerContextValue {
  const ctx = useContext(JustCallDialerContext);
  if (!ctx) {
    throw new Error('useJustCallDialer must be used within JustCallDialerProvider');
  }
  return ctx;
}
